import React, { useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import AutoCompleteField from '@components/common/form/AutoCompleteField';
import {
    CHANGE_DEVICE_STATUS_CODE,
    DATE_FORMAT_DISPLAY,
    DATE_FORMAT_VALUE,
    DEFAULT_FORMAT,
    ORDER_DISPLAY_LAYOUT_1,
    STATUS_PENDING,
} from '@constants';
import apiConfig from '@constants/apiConfig';
import { deviceStatus, orderDisplayLayouts, printerTypeOptions, statusOptions } from '@constants/masterData';
import useAuth from '@hooks/useAuth';
import useBasicForm from '@hooks/useBasicForm';
import useTranslate from '@hooks/useTranslate';
import { BaseForm, CheckboxField, DatePickerField, SelectField, TextField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { getDefaultPrinterSetting } from '@utils';
import { Card, Col, Row } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

const message = defineMessages({
    requiredExpireDate: 'Expiration date is required',
    expireDateMustBeFuture: 'Expiration date must be a future date',
});

const DeviceKitplayForm = ({ formId, actions, dataDetail, onSubmit, setIsChangedFormValues, isEditing, deviceId }) => {
    const translate = useTranslate();
    const statusValues = translate.formatKeys(deviceStatus, ['label']);
    const layoutValues = translate.formatKeys(orderDisplayLayouts, ['label']);
    const printerTypeValues = translate.formatKeys(printerTypeOptions, ['label']);
    const { profile } = useAuth();

    const permissionCodes = profile?.group?.permissions?.map((permission) => permission.pcode);
    const hasPermissionCode = permissionCodes?.includes(CHANGE_DEVICE_STATUS_CODE);

    const { form, mixinFuncs, onValuesChange } = useBasicForm({
        onSubmit,
        setIsChangedFormValues,
    });

    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const device_id = deviceId ?? queryParams.get('deviceId');
    const params = useParams();
    const handleSubmit = (values) => {
        values.restaurantId = params.customerId;
        if (values.expireDate) {
            values.expireDate = dayjs(values.expireDate).endOf('day').utc().format(DEFAULT_FORMAT);
        }
        values.printer = {
            ...dataDetail?.printer,
            doneToIpPrinter: values.doneToIpPrinter,
            sortId: 1,
            areasId: values.areasId,
            numberOfColumns: values.numberOfColumns,
            type: values.printer?.type,
            id: dataDetail ? dataDetail?.printer?.id : null,
        };
        delete values.areasId;
        delete values.numberOfColumns;
        delete values.doneToIpPrinter;

        values.type = 2;
        const settingsObj = !isEditing
            ? JSON.parse(getDefaultPrinterSetting())
            : dataDetail?.settings
                ? JSON.parse(dataDetail.settings)
                : {};
        settingsObj.win_print = values.winPrint ?? false;
        settingsObj.layout = values.layout ?? ORDER_DISPLAY_LAYOUT_1;
        const finalValues = {
            ...values,
            settings: JSON.stringify(settingsObj),
        };
        delete finalValues.winPrint;
        delete finalValues.layout;
        return mixinFuncs.handleSubmit(finalValues);
    };

    useEffect(() => {
        dataDetail.expireDate = dataDetail.expireDate && dayjs(dataDetail.expireDate, DATE_FORMAT_VALUE);
        form.setFieldsValue({
            ...dataDetail,
            doneToIpPrinter: dataDetail?.printer?.doneToIpPrinter,
            numberOfColumns: dataDetail?.printer?.numberOfColumns,
            sortId: dataDetail?.printer?.sortId,
            areasId: dataDetail?.printer?.area?.id,
            status: dataDetail?.status,
            winPrint: dataDetail?.settings ? JSON.parse(dataDetail.settings).win_print : false,
            layout: dataDetail?.settings ? JSON.parse(dataDetail.settings).layout ?? ORDER_DISPLAY_LAYOUT_1 : ORDER_DISPLAY_LAYOUT_1,
        });
    }, [dataDetail]);

    useEffect(() => {
        if (!isEditing) {
            form.setFieldsValue({
                status: STATUS_PENDING,
                winPrint: false,
                layout: ORDER_DISPLAY_LAYOUT_1,
            });
        }
    }, [isEditing]);

    return (
        <BaseForm id={formId} onFinish={handleSubmit} form={form} onValuesChange={onValuesChange}>
            <Card className="card-form" bordered={false}>
                <Row gutter={10}>
                    <Col span={12}>
                        <TextField required label={<FormattedMessage defaultMessage="Device name" />} name="name" />
                    </Col>
                    <Col span={12}>
                        <TextField
                            required
                            label={<FormattedMessage defaultMessage="Device ID" />}
                            name="deviceId"
                        // disabled={isEditing}
                        />
                    </Col>
                </Row>
                <Row gutter={10} align="bottom">
                    <Col span={12}>
                        <AutoCompleteField
                            label={<FormattedMessage defaultMessage="Area" />}
                            name={['areasId']}
                            apiConfig={apiConfig.areas.autocomplete}
                            mappingOptions={(item) => ({ value: item.id, label: item.name })}
                            initialSearchParams={{ deviceId: device_id }}
                            options={isEditing && dataDetail?.printer?.area ? [
                                { value: dataDetail.printer.area.id, label: dataDetail.printer.area.name },
                            ] : undefined}
                            searchParams={(text) => ({ name: text, deviceId: device_id })}
                            required
                        />
                    </Col>
                    <Col span={8}>
                        <TextField
                            label={<FormattedMessage defaultMessage="Physical printer IP" />}
                            name="doneToIpPrinter"
                        />
                    </Col>
                    <Col span={4}>
                        <CheckboxField
                            label={<FormattedMessage defaultMessage="WinPrint" />}
                            name="winPrint"
                        />
                    </Col>
                </Row>

                <Row gutter={10}>
                    <Col span={12}>
                        <DatePickerField
                            label={<FormattedMessage defaultMessage="Expiration date" />}
                            name="expireDate"
                            format={DATE_FORMAT_DISPLAY}
                            disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
                            // rules={[
                            //     {
                            //         required: true,
                            //         message: translate.formatMessage(message.requiredExpireDate),
                            //     },
                            //     {
                            //         validator: (_, value) => {
                            //             if (value && value.isBefore(dayjs(), 'day')) {
                            //                 return Promise.reject(translate.formatMessage(message.expireDateMustBeFuture));
                            //             }
                            //             return Promise.resolve();
                            //         },
                            //     },
                            // ]}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col span={12}>
                        <SelectField
                            required
                            label={<FormattedMessage defaultMessage="Number of columns" />}
                            name={['numberOfColumns']}
                            options={[...Array(6)]
                                .map((_, index) => ({
                                    value: index + 1,
                                    label: `${index + 1}`,
                                }))
                                .slice(2, 6)}
                        />
                    </Col>
                    <Col span={12}>
                        <SelectField
                            required
                            label={<FormattedMessage defaultMessage="Status" />}
                            name="status"
                            options={statusValues}
                            disabled={!hasPermissionCode}
                        />
                    </Col>
                    <Col span={12}>
                        <SelectField
                            required
                            label={<FormattedMessage defaultMessage="Layout" />}
                            name="layout"
                            options={layoutValues}
                        />
                    </Col>
                    <Col span={12}>
                        <SelectField
                            required
                            label={translate.formatMessage(commonMessage.printerType)}
                            name={['printer', 'type']}
                            options={printerTypeValues}
                            allowClear={false}
                        />
                    </Col>
                </Row>
                <div className="footer-card-form">{actions}</div>
            </Card>
        </BaseForm>
    );
};

export default DeviceKitplayForm;
